import { Paper, Typography } from "@mui/material";
import { CustomGridColDef, DataGrid } from "components/DataGrid";
import { useMemo } from "react";

export interface PrePostRow {
  key: string;
  name: string;
  priorSubmittals: number;
  priorRatePct: number | null;
  afterSubmittals: number;
  afterRatePct: number | null;
  deltaPct: number | null;
}

export const formatPct = (value: number | null | undefined) => (value == null ? "—" : `${value}%`);
export const formatDelta = (value: number | null | undefined) => {
  if (value == null) return "—";
  const sign = value > 0 ? "+" : "";
  return `${sign}${value} pp`;
};

// Rows arrive pre-sorted from the API (delta desc); the grid's client-side
// column sorting can re-order from there.
export function PrePostTable({
  title,
  nameHeader,
  rows,
  loading,
}: {
  title: string;
  nameHeader: string;
  rows: PrePostRow[];
  loading: boolean;
}) {
  // All columns flex so a narrow viewport compresses them instead of
  // virtualizing the After/Delta columns out of view — the delta IS the point
  // of these tables, and the grid gives no visual hint that columns are
  // hidden off-scroll.
  const columns: CustomGridColDef<PrePostRow>[] = useMemo(
    () => [
      { field: "name", headerName: nameHeader, flex: 2, minWidth: 140 },
      {
        field: "priorSubmittals",
        headerName: "Prior Submittals",
        type: "number",
        flex: 1,
        minWidth: 110,
      },
      {
        field: "priorRatePct",
        headerName: "Prior Rate",
        type: "number",
        flex: 1,
        minWidth: 90,
        valueFormatter: (value: number | null) => formatPct(value),
      },
      {
        field: "afterSubmittals",
        headerName: "After Submittals",
        type: "number",
        flex: 1,
        minWidth: 110,
      },
      {
        field: "afterRatePct",
        headerName: "After Rate",
        type: "number",
        flex: 1,
        minWidth: 90,
        valueFormatter: (value: number | null) => formatPct(value),
      },
      {
        field: "deltaPct",
        headerName: "Delta",
        type: "number",
        flex: 1,
        minWidth: 90,
        valueFormatter: (value: number | null) => formatDelta(value),
      },
    ],
    [nameHeader]
  );

  return (
    <Paper elevation={0} sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        {title}
      </Typography>
      <DataGrid
        autoHeight
        rows={rows}
        columns={columns}
        getRowId={(row: PrePostRow) => row.key}
        loading={loading}
        pagination
        initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
        pageSizeOptions={[25, 50, 100]}
        disableColumnMenu
        noRowsMessage="No eligible submittals."
        disableRowSelectionOnClick
        // Rows here are read-only aggregates — override the shared wrapper's
        // hover pointer so the cursor doesn't promise a click that does nothing.
        sx={{ "& .MuiDataGrid-row:hover": { cursor: "default" } }}
      />
    </Paper>
  );
}
